import { domainByCountry } from '@models/common/outsetaRealm.enum';
import { IOutsetaToken } from '@models/outseta/outseta';
import { jwtDecode } from 'jwt-decode';

export const OUTSETA_TOKEN_STORAGE_KEY = 'Outseta.nocode.accessToken';

// Decoding on every render would be wasteful (ChatProvider re-renders per streamed chunk), and the
// token changes rarely, so the last decode is kept and reused while the raw token is unchanged.
let lastToken: string | null = null;
let lastMemberUid: string | null = null;

/**
 * Uid of the member the current access token belongs to — the same value the backend authorises
 * with. jooice-ai's Outseta strategy reads `uid: payload.sub ?? ''` and its OwnershipChecker
 * compares that against the conversation's `metadata.userId`, so `sub` is the authority and there
 * is deliberately no fallback to another claim here: scoping by a claim the backend does not read
 * would key the cache by something other than the owner it will be checked against.
 *
 * Read this rather than `useOutseta().user.Uid` wherever a request is scoped to its owner.
 * `OutsetaProvider` only refreshes `user` on an Outseta subscription/profile event, so during
 * impersonation the token can already belong to the impersonated member while `user` still
 * describes the previous one — and anything cached under the stale uid then belongs to the wrong
 * member.
 */
export const getMemberUidFromToken = (): string | null => {
  const token = localStorage.getItem(OUTSETA_TOKEN_STORAGE_KEY);
  if (token === lastToken) return lastMemberUid;

  lastToken = token;
  lastMemberUid = null;

  if (token) {
    try {
      const decoded: IOutsetaToken = jwtDecode(token);
      lastMemberUid = decoded.sub || null;
    } catch {
      // A crafted or truncated value in localStorage must not break the caller.
      lastMemberUid = null;
    }
  }

  return lastMemberUid;
};

declare global {
  interface Window {
    o_options: {
      domain: string;
      monitorDom: boolean;
      tokenStorage: string;
      load: string;
      profile: {
        mode: string;
        selector: string;
        tabs: string;
      };
    };
  }
}

let outsetaInitPromise: Promise<boolean> | null = null;
let currentDomain: string | null = null;

export const clearOutsetaLocalStorage = (domain: string) => {
  const prefix = `outseta.auth--${domain}`;
  Object.keys(localStorage)
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => localStorage.removeItem(key));
};

export const initializeOutsetaProfile = async (countryCode: string) => {
  const requestedDomain = domainByCountry[countryCode as keyof typeof domainByCountry] || domainByCountry.usa;

  const scriptInDom = document.querySelector('script[src*="outseta.min.js"]');
  // Reset if domain changed, or if script was loaded externally (e.g. OutsetaEmbed for Google login)
  // with a different domain (currentDomain === null means this module never loaded it)
  const domainMismatch = currentDomain !== null && currentDomain !== requestedDomain;
  const externalDomainMismatch =
    currentDomain === null && scriptInDom !== null && window.o_options?.domain !== requestedDomain;
  if (domainMismatch || externalDomainMismatch) {
    outsetaInitPromise = null;
    if (scriptInDom) {
      scriptInDom.remove();
    }
  }

  // Return cached promise if exists for same domain
  if (outsetaInitPromise && currentDomain === requestedDomain) {
    return outsetaInitPromise;
  }

  // Store the new domain
  currentDomain = requestedDomain;

  // Create new promise
  outsetaInitPromise = new Promise((resolve, reject) => {
    // Script already in DOM (loaded by OutsetaEmbed or a prior call)
    const existingScript = document.querySelector('script[src*="outseta.min.js"]');
    if (existingScript) {
      existingScript.addEventListener(
        'load',
        () => {
          console.warn(
            '[Outseta] existing script load fired, window.Outseta:',
            (window as unknown as { Outseta?: unknown }).Outseta,
          );
          resolve(true);
        },
        { once: true },
      );
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Outseta.js')), { once: true });
      return;
    }

    // Only proceed if we have a token
    const token = localStorage.getItem('Outseta.nocode.accessToken');
    if (!token) {
      reject(new Error('No token available'));
      return;
    }

    const domain = domainByCountry[countryCode as keyof typeof domainByCountry] || domainByCountry.usa;

    // Configure Outseta options
    window.o_options = {
      domain,
      monitorDom: true,
      tokenStorage: 'local',
      load: 'auth,nocode,profile',
      profile: {
        mode: 'embed',
        selector: '#profile-embed',
        tabs: 'account,plan,billing',
      },
    };

    const script = document.createElement('script');
    script.src = 'https://cdn.outseta.com/outseta.min.js';
    script.setAttribute('data-options', 'o_options');
    // onload fires after the script has fully executed, so window.Outseta is guaranteed to be set
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Outseta.js'));

    document.head.appendChild(script);
  });

  return outsetaInitPromise;
};
